import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import s from '../CaseFilePage.module.css';

interface StageHistoryEntry {
  id: number;
  old_stage_name?: string | null;
  new_stage_name: string;
  changed_at: string;
  changed_by?: string;
  change_source?: string;
  days_in_stage?: number | null;
  hours_in_stage?: number | null;
}

interface StageHistoryModalProps {
  caseRef: string;
  stageHistory: StageHistoryEntry[];
  onClose: () => void;
}

export default function StageHistoryModal({
  caseRef,
  stageHistory,
  onClose,
}: StageHistoryModalProps) {
  return (
    <ModalOverlay onClose={onClose} title={`Stage History — ${caseRef}`}>
      {stageHistory.length === 0 ? (
        <p className={s.emptyMsg}>No stage history available.</p>
      ) : (
        <div className={s.stageTimeline}>
          {stageHistory.map((entry, idx) => {
            const isLast = idx === stageHistory.length - 1;
            const isFirst = idx === 0;
            const changedAt = new Date(entry.changed_at);
            const formattedDate = changedAt.toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            });
            const formattedTime = changedAt.toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
            });

            let durationText = '';
            if (isLast) {
              durationText = 'Current stage';
            } else if (entry.days_in_stage != null) {
              const days = Math.floor(entry.days_in_stage);
              const hours = Math.floor((entry.hours_in_stage ?? 0) % 24);
              if (days > 0)
                durationText = `Duration: ${days} day${days !== 1 ? 's' : ''}${hours > 0 ? ` ${hours}h` : ''}`;
              else if (hours > 0) durationText = `Duration: ${hours} hour${hours !== 1 ? 's' : ''}`;
              else durationText = 'Duration: < 1 hour';
            }

            const src = entry.change_source || 'unknown';
            const sourceMap: Record<string, { icon: string; label: string; color: string }> = {
              manual: { icon: '👤', label: 'Manual change', color: 'var(--info-600)' },
              bulk_update: { icon: '📊', label: 'Bulk update', color: '#f59e0b' },
              dropbox: { icon: '📥', label: 'Form submission', color: 'var(--success-600)' },
              dropbox_submission: {
                icon: '📥',
                label: 'Form submission',
                color: 'var(--success-600)',
              },
              system: { icon: '⚙️', label: 'System', color: 'var(--gray-500)' },
              direct_sql: { icon: '🔧', label: 'Direct SQL', color: 'var(--error-600)' },
            };
            const srcInfo = sourceMap[src] || { icon: '●', label: src, color: 'var(--info-600)' };

            return (
              <div
                key={entry.id}
                className={`${s.stageTimelineItem} ${isLast ? s.stageTimelineCurrent : ''}`}
              >
                <div
                  className={s.stageTimelineMarker}
                  style={{
                    borderColor: srcInfo.color,
                    background: isLast ? srcInfo.color : 'var(--surface-primary)',
                  }}
                >
                  {isLast && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      style={{ fontSize: '0.5rem', color: 'var(--text-on-primary)' }}
                    />
                  )}
                </div>
                {!isLast && <div className={s.stageTimelineLine} />}
                <div className={s.stageTimelineCard}>
                  <div className={s.stageTimelineHeader}>
                    <div>
                      <strong className={s.stageTimelineStageName}>{entry.new_stage_name}</strong>
                      {isFirst && <span className={s.stageTimelineInitial}>(Initial)</span>}
                      {isLast && <span className={s.stageTimelineCurrentBadge}>● CURRENT</span>}
                    </div>
                    <div className={s.stageTimelineDate}>
                      {formattedDate}
                      <br />
                      {formattedTime}
                    </div>
                  </div>
                  {entry.old_stage_name && (
                    <div className={s.stageTimelineFrom}>
                      <span style={{ color: 'var(--gray-400)' }}>From:</span> {entry.old_stage_name}
                    </div>
                  )}
                  <div className={s.stageTimelineFooter}>
                    <div>
                      <span style={{ marginRight: 10 }}>
                        {srcInfo.icon} {srcInfo.label}
                      </span>
                      {entry.changed_by && (
                        <span>
                          <span style={{ color: 'var(--gray-400)' }}>by</span>{' '}
                          <strong>{entry.changed_by}</strong>
                        </span>
                      )}
                    </div>
                    {durationText && (
                      <span className={s.stageTimelineDuration}>{durationText}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ModalOverlay>
  );
}
